import { scopedSetter } from "../api/scopedSetter.js";
import { create } from "zustand";
import { t } from "../i18n/index.js";
import { toast } from "../ui/Toast.js";
import { errorText } from "../base/state.js";
import { useUnits } from "../units/state.js";
import { useSession } from "../state/session.js";
import { dmChannel } from "@zend/rules";
import { boardsApi, chatApi, mailApi, profilesApi, socialApi } from "./api.js";
import type { Board, ChatCard, ChatMessage, ChatState, Contact, MailList, Neighbour, ProfileCard, ProfileOptions } from "./types.js";
import { filterKinds, type MailFilter } from "./mailModel.js";

export type SocialTab = "chat" | "mail" | "boards" | "neighbours";
export const SOCIAL_TABS: SocialTab[] = ["chat", "mail", "boards", "neighbours"];

interface SocialStore {
  chat: ChatState | null;
  channel: string;
  messages: Record<string, ChatMessage[]>;
  replyTo: ChatMessage | null;
  /** DM opened from a profile or list before any message exists: the tab needs the partner's name. */
  pendingDm: { channel: string; accountId: string; name: string } | null;
  /** The conversation pane is open (below 900 px it replaces the list); desktop shows both (D67). */
  channelOpen: boolean;
  /** Unread count per channel captured when it was opened: places the unread divider (D67). */
  unreadAtOpen: Record<string, number>;
  mail: MailList | null;
  mailFilter: string;
  mailTrash: boolean;
  board: Board | null;
  boardId: string;
  neighbours: Neighbour[] | null;
  contacts: Contact[] | null;
  profile: ProfileCard | null;
  profileOptions: ProfileOptions | null;
  profileLoading: boolean;
  unread: number;
  mailUnread: number;
  refreshChat(): Promise<void>;
  openChannel(channel: string): Promise<void>;
  closeConversation(): void;
  openDm(accountId: string, name: string): Promise<void>;
  loadOlder(): Promise<void>;
  send(text: string, card?: ChatCard): Promise<boolean>;
  setReplyTo(m: ChatMessage | null): void;
  pushMessage(m: ChatMessage): void;
  markDeleted(id: string, channel: string): void;
  refreshMail(): Promise<void>;
  setMailFilter(kind: string, trash: boolean): void;
  refreshBoard(id?: string): Promise<void>;
  refreshNeighbours(): Promise<void>;
  openProfile(accountId: string): Promise<void>;
  closeProfile(): void;
  refreshProfileOptions(): Promise<void>;
  run<T>(op: () => Promise<T>, okText?: string): Promise<T | null>;
}

export const useSocial = create<SocialStore>((set, get) => ({
  chat: null,
  channel: "global",
  messages: {},
  replyTo: null,
  pendingDm: null,
  channelOpen: false,
  unreadAtOpen: {},
  mail: null,
  mailFilter: "all",
  mailTrash: false,
  board: null,
  boardId: "power",
  neighbours: null,
  contacts: null,
  profile: null,
  profileOptions: null,
  profileLoading: false,
  unread: 0,
  mailUnread: 0,
  refreshChat: async () => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    try {
      const chat = await chatApi.state();
      set({ chat, unread: chat.unread.global + chat.unread.dm + get().mailUnread });
    } catch {
      /* logged out */
    }
  },
  openChannel: async (channel) => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    set({ channel, replyTo: null, channelOpen: true });
    try {
      const h = await chatApi.history(channel);
      // The history reports the unread count it found before marking the channel read: it places the divider (D67).
      set((s) => ({ messages: { ...s.messages, [channel]: h.messages }, unreadAtOpen: { ...s.unreadAtOpen, [channel]: h.unread } }));
      if (!set.current()) return;
      await get().refreshChat();
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  openDm: async (accountId, name) => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    const me = useSession.getState().account;
    if (!me) return;
    const channel = dmChannel(me.id, accountId);
    set({ pendingDm: { channel, accountId, name } });
    await get().openChannel(channel);
  },
  loadOlder: async () => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    const { channel, messages } = get();
    const first = messages[channel]?.[0];
    if (!first) return;
    const h = await chatApi.history(channel, first.createdAt);
    set((s) => ({ messages: { ...s.messages, [channel]: [...h.messages, ...(s.messages[channel] ?? [])] } }));
  },
  send: async (text, card) => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    const { channel, replyTo } = get();
    try {
      const m = await chatApi.send(channel, text, { ...(replyTo ? { replyTo: replyTo.id } : {}), ...(card ? { card } : {}) });
      if (!set.current()) return false;
      get().pushMessage(m);
      set({ replyTo: null });
      return true;
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
      return false;
    }
  },
  closeConversation: () => set({ channelOpen: false }),
  setReplyTo: (replyTo) => set({ replyTo }),
  pushMessage: (m) => {
    set((s) => {
      const list = s.messages[m.channel] ?? [];
      if (list.some((x) => x.id === m.id)) return {};
      return { messages: { ...s.messages, [m.channel]: [...list, m].slice(-300) } };
    });
    const st = get();
    if (m.channel !== st.channel && st.chat) {
      const chat = { ...st.chat, unread: { ...st.chat.unread } };
      if (m.channel === "global") chat.unread.global++;
      else if (m.channel === "system") chat.unread.system++;
      else chat.unread.dm++;
      set({ chat, unread: chat.unread.global + chat.unread.dm + st.mailUnread });
    }
  },
  markDeleted: (id, channel) => set((s) => ({ messages: { ...s.messages, [channel]: (s.messages[channel] ?? []).map((m) => (m.id === id ? { ...m, deleted: true, text: "", segments: [], card: null } : m)) } })),
  refreshMail: async () => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    try {
      const { mailFilter, mailTrash } = get();
      const kinds = filterKinds(mailFilter as MailFilter);
      const mail = await mailApi.list({ ...(kinds ? { kind: kinds.join(",") } : {}), trash: mailTrash });
      const chat = get().chat;
      set({ mail, mailUnread: mail.unread, unread: (chat?.unread.global ?? 0) + (chat?.unread.dm ?? 0) + mail.unread });
    } catch {
      /* logged out */
    }
  },
  setMailFilter: (mailFilter, mailTrash) => {
    set({ mailFilter, mailTrash });
    void get().refreshMail();
  },
  refreshBoard: async (id) => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    const boardId = id ?? get().boardId;
    set({ boardId });
    try {
      set({ board: await boardsApi.board(boardId) });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  refreshNeighbours: async () => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    try {
      const [neighbours, contacts] = await Promise.all([socialApi.neighbours(), socialApi.contacts()]);
      set({ neighbours, contacts });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  openProfile: async (accountId) => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    set({ profileLoading: true });
    try {
      const profile = await profilesApi.card(accountId);
      set({ profile, profileLoading: false });
    } catch (e) {
      set({ profileLoading: false });
      if (set.current()) toast.error(errorText(e));
    }
  },
  closeProfile: () => set({ profile: null, profileOptions: null }),
  refreshProfileOptions: async () => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    try {
      set({ profileOptions: await profilesApi.options() });
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
    }
  },
  run: async (op, okText) => {
    const set = scopedSetter<SocialStore>(useSocial.setState);
    try {
      const result = await op();
      if (!set.current()) return null;
      await useUnits.getState().refreshMe();
      if (!set.current()) return null;
      if (okText) toast.success(okText);
      return result;
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
      return null;
    }
  },
}));

export function lastSeenText(ls: { kind: string; n?: number }): string {
  switch (ls.kind) {
    case "online":
      return t("social.lastSeen.online");
    case "minutes":
      return t("social.lastSeen.minutes", { n: ls.n ?? 0 });
    case "hours":
      return t("social.lastSeen.hours", { n: ls.n ?? 0 });
    case "days":
      return t("social.lastSeen.days", { n: ls.n ?? 0 });
    default:
      return t("social.lastSeen.never");
  }
}
